import { useRef, useState, useCallback, useEffect } from 'react';
import { triggerPdfExport, pollPdfStatus } from '../lib/orders-api';

export function usePdfExport() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const startPdfJob = useCallback((): Promise<string | null> => {
    return new Promise((resolve) => {
      setLoading(true);
      setError(null);

      triggerPdfExport()
        .then(({ jobId }) => {
          const poll = (attempt: number) => {
            if (cancelledRef.current) {
              resolve(null);
              return;
            }
            if (attempt >= 60) {
              setLoading(false);
              setError('PDF generation timed out — try again.');
              resolve(null);
              return;
            }
            pollPdfStatus(jobId)
              .then((status) => {
                if (cancelledRef.current) {
                  resolve(null);
                  return;
                }
                if (status.status === 'complete') {
                  setLoading(false);
                  resolve(status.downloadUrl ?? null);
                } else if (status.status === 'error' || status.status === 'failed') {
                  setLoading(false);
                  setError('PDF generation failed — try again.');
                  resolve(null);
                } else {
                  setTimeout(() => poll(attempt + 1), 3000);
                }
              })
              .catch(() => {
                if (!cancelledRef.current) {
                  setLoading(false);
                  setError('PDF generation failed — try again.');
                }
                resolve(null);
              });
          };
          setTimeout(() => poll(0), 3000);
        })
        .catch(() => {
          if (!cancelledRef.current) {
            setLoading(false);
            setError('PDF generation failed — try again.');
          }
          resolve(null);
        });
    });
  }, []);

  const handleDownload = useCallback(async (): Promise<void> => {
    if (loading) return;
    const url = await startPdfJob();
    if (url) {
      window.open(url, '_blank');
    }
  }, [loading, startPdfJob]);

  const handleShare = useCallback(async (): Promise<void> => {
    if (loading) return;
    const url = await startPdfJob();
    if (url) {
      navigator.clipboard.writeText(url).catch(() => {
        prompt('Copy this link:', url);
      });
      setCopied(true);
      setTimeout(() => {
        if (!cancelledRef.current) setCopied(false);
      }, 2000);
    }
  }, [loading, startPdfJob]);

  return { loading, error, copied, handleDownload, handleShare };
}
